import { useMemo, useState } from 'react'
import { AddOutlined as AddOutlinedIcon, UploadFileOutlined as UploadFileOutlinedIcon } from '@mui/icons-material'
import DataTable from '../../components/DataTable'
import SectionCard from '../../components/SectionCard'
import { uploadModes } from '../../constants/inventoryModes'
import { formatDate } from '../../utils/inventoryFormatters'

const getUploadModeMeta = mode => uploadModes.find(option => option.key === mode) || uploadModes[0]

const getUploadResultSummary = row => {
  if (row.mode === 'items') {
    return `Insertados: ${row.inserted ?? 0} · Actualizados: ${row.updated ?? 0} · Errores: ${row.errors ?? 0}`
  }

  return `Procesadas: ${row.processed ?? 0} · Movimientos: ${row.movementsCreated ?? 0} · Errores: ${row.errors ?? 0}`
}

const UploadStatusPill = ({ errors }) => {
  const hasErrors = Number(errors || 0) > 0

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
        hasErrors
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-emerald-200 bg-emerald-50 text-emerald-700'
      }`}
    >
      {hasErrors ? 'Con errores' : 'Completada'}
    </span>
  )
}

const UploadsSection = ({ navigation, state }) => {
  const [searchValue, setSearchValue] = useState('')
  const [modeFilter, setModeFilter] = useState('')

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase()

    return state.uploadHistory.filter(row => {
      const matchesMode = modeFilter ? row.mode === modeFilter : true
      const haystack = `${row.fileName || ''} ${row.batchCode || ''} ${row.mode || ''}`.toLowerCase()
      const matchesSearch = normalizedSearch ? haystack.includes(normalizedSearch) : true

      return matchesMode && matchesSearch
    })
  }, [modeFilter, searchValue, state.uploadHistory])

  return (
    <SectionCard
      title='Carga Excel'
      description='Explora el historial de cargas ejecutadas y abre un modal para iniciar una nueva importación.'
    >
      <div className='space-y-4'>
        <div className='flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 lg:flex-row lg:items-end lg:justify-between'>
          <div className='grid w-full grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_220px] lg:max-w-3xl'>
            <div>
              <label className='mb-1 block text-xs font-semibold text-slate-700'>Buscar carga</label>
              <input
                type='search'
                value={searchValue}
                onChange={event => setSearchValue(event.target.value)}
                className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                placeholder='Buscar por archivo, batch o tipo'
              />
            </div>

            <div>
              <label className='mb-1 block text-xs font-semibold text-slate-700'>Filtrar por tipo</label>
              <select
                value={modeFilter}
                onChange={event => setModeFilter(event.target.value)}
                className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
              >
                <option value=''>Todos los tipos</option>
                {uploadModes.map(mode => (
                  <option key={mode.key} value={mode.key}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type='button'
            onClick={() => navigation.openUploadModal('items')}
            className='inline-flex items-center justify-center gap-1.5 rounded-lg bg-accent2 px-4 py-2 text-sm font-semibold text-white transition hover:bg-accent2/90'
          >
            <AddOutlinedIcon style={{ fontSize: '1rem' }} />
            Nueva carga Excel
          </button>
        </div>

        <DataTable
          columns={['Tipo', 'Fecha', 'Archivo', 'Resultado', 'Batch', 'Estado', 'Acciones']}
          rows={filteredRows}
          emptyText='Aún no hay cargas registradas en esta sesión. Usa "Nueva carga Excel" para comenzar.'
          renderRow={row => {
            const modeMeta = getUploadModeMeta(row.mode)
            const IconComponent = modeMeta.icon

            return (
              <>
                <td className='px-4 py-3 align-top'>
                  <div className='flex items-start gap-2'>
                    <div className='mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500'>
                      <IconComponent style={{ fontSize: '0.95rem' }} />
                    </div>
                    <div>
                      <div className='font-semibold text-slate-900'>{modeMeta.label}</div>
                      <div className='mt-1 text-xs text-slate-500'>{modeMeta.description}</div>
                    </div>
                  </div>
                </td>
                <td className='px-4 py-3 align-top text-slate-700'>{formatDate(row.createdAt)}</td>
                <td className='px-4 py-3 align-top text-slate-700'>{row.fileName || 'Sin archivo'}</td>
                <td className='px-4 py-3 align-top text-slate-700'>{getUploadResultSummary(row)}</td>
                <td className='px-4 py-3 align-top text-slate-700'>{row.batchCode || '-'}</td>
                <td className='px-4 py-3 align-top'>
                  <UploadStatusPill errors={row.errors} />
                </td>
                <td className='px-4 py-3 align-top'>
                  <div className='flex justify-end'>
                    <button
                      type='button'
                      onClick={() => navigation.openUploadModal(row.mode)}
                      className='inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50'
                    >
                      <UploadFileOutlinedIcon style={{ fontSize: '0.9rem' }} />
                      Repetir
                    </button>
                  </div>
                </td>
              </>
            )
          }}
        />
      </div>
    </SectionCard>
  )
}

export default UploadsSection
